/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Landmark, ArrowRight, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { getConnectStatus } from '@/lib/telemedicine';

/**
 * StripeConnectCTA — sticky pill in the TopBar that reminds professionals to
 * complete Stripe Connect KYC when their account isn't ready to receive payouts.
 *
 * Rules:
 *  - Only shown to professionals (patients never see it).
 *  - Hidden when Connect is ready (ready === true).
 *  - Hidden when the user is already on the Doctor Command Center (Telemedicine page).
 *  - Dismissable per session via sessionStorage.
 */
const StripeConnectCTA = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isProfessional = user && user.role !== 'patient' && user.category !== 'paziente';
  const isOnTelemedicine = location.pathname.startsWith('/telemedicine');

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem('sc_stripeCTA_dismissed') === '1'); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isProfessional) { setLoading(false); return; }
    (async () => {
      try {
        const s = await getConnectStatus();
        if (!cancelled) setStatus(s);
      } catch (e) {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isProfessional]);

  const handleDismiss = (e) => {
    e?.stopPropagation();
    try { sessionStorage.setItem('sc_stripeCTA_dismissed', '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  if (!isProfessional || loading || dismissed || isOnTelemedicine) return null;
  if (!status) return null;
  // Ready → no CTA.
  if (status.ready) return null;

  const noAccount = !status.account_id;
  const label = noAccount ? 'Attiva pagamenti' : 'Completa KYC Stripe';
  const detail = noAccount
    ? 'Ricevi pagamenti dai pazienti'
    : `${(status.requirements_due || []).length || 'Dati mancanti'} da completare`;
  const tone = noAccount ? 'accent' : 'warning';

  return (
    <AnimatePresence>
      <motion.button
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.18 }}
        onClick={() => navigate('/telemedicine')}
        aria-label={label}
        className={`group inline-flex items-center justify-center gap-2 rounded-full h-9 w-9 p-0 sm:h-auto sm:w-auto sm:px-3 sm:py-1.5 text-xs font-semibold transition-colors border ${
          tone === 'accent'
            ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15'
            : 'bg-amber-500/10 text-amber-700 border-amber-500/30 hover:bg-amber-500/15'
        }`}
        data-testid="stripe-connect-cta"
        title={detail}
      >
        {tone === 'accent' ? <Landmark className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
        {/* Mobile (<sm): solo icona, per non far sforare la top bar. Desktop: etichetta completa. */}
        <span className="hidden sm:inline">{label}</span>
        <ArrowRight className="hidden sm:inline h-3.5 w-3.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
        <span
          role="button"
          tabIndex={0}
          onClick={handleDismiss}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDismiss(e); }}
          className="hidden sm:flex ml-1 -mr-1 h-5 w-5 rounded-full items-center justify-center hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          data-testid="stripe-connect-cta-dismiss"
          aria-label="Nascondi"
        >
          <X className="h-3 w-3" />
        </span>
      </motion.button>
    </AnimatePresence>
  );
};

export default StripeConnectCTA;
